import React from 'react';
import { debounce } from 'lodash';
import { getNameForUserUuid, createNewCoach } from 'coaching/actions/CoachingActions';
import { assertions } from 'lib/assertions';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CheckboxList from 'components/CheckboxList';
import ErrorReporter from 'app/components/ErrorReporter';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Input from 'components/Input';
import LoadingIndicator from 'components/LoadingIndicator';
import Row from 'components/Row';
import Text from 'components/Text';
import Select, { Option } from 'components/Select';

export type Props = {
  loadingCoachableSubjects: boolean;
  error?: string;
  subjects?: string[];
  hourlyRatesInCents?: number[];
};

type State = {
  coachUuid?: string;
  coachName?: string;
  loadingCoachName: boolean;
  selectedSubjects: string[];
  submittingNewCoach: boolean;
  hourlyRateInCents?: number;
};

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export default class CoachingCoordinatorAddCoachSidebar extends React.Component<Props, State> {
  state: State = {
    submittingNewCoach: false,
    loadingCoachName: false,
    selectedSubjects: [],
  };

  handleAddCoach = () => {
    const { coachUuid, hourlyRateInCents } = this.state;
    if (!coachUuid) {
      console.error(
        'CoachingCoordinatorAddCoachSidebar - tried to set coach name without coachUuid'
      );
      return;
    }
    if (!hourlyRateInCents) {
      console.error(
        'CoachingCoordinatorAddCoachSidebar - tried to set coach name without hourly rate'
      );
      return;
    }

    this.setState({ submittingNewCoach: true });
    return createNewCoach(coachUuid, this.state.selectedSubjects, hourlyRateInCents).then(
      this.resetCoachState,
      () => this.setState({ submittingNewCoach: false })
    );
  };

  resetCoachState = () => {
    this.setState({
      loadingCoachName: false,
      selectedSubjects: [],
      coachName: undefined,
      coachUuid: undefined,
      hourlyRateInCents: undefined,
      submittingNewCoach: false,
    });
  };

  setCoachUuidAndName = (coachUuid: string) => {
    this.setState({ coachUuid }, this.fetchAndSetCoachName);
  };

  fetchAndSetCoachName = debounce(() => {
    const { coachUuid } = this.state;
    if (!coachUuid) {
      console.error(
        'CoachingCoordinatorAddCoachSidebar - tried to set coach name without coachUuid'
      );
      return;
    }

    this.setState({ loadingCoachName: true });
    return getNameForUserUuid(coachUuid).then(name =>
      this.setState({ coachName: name, loadingCoachName: false })
    );
  }, 500);

  renderCreateCoachFields = () => {
    const { loadingCoachableSubjects, hourlyRatesInCents, error, subjects } = this.props;
    if (loadingCoachableSubjects) {
      return (
        <Row>
          <CenteredLoadingIndicator />
        </Row>
      );
    }
    if (error || !subjects || !hourlyRatesInCents) {
      return (
        <Row>
          <ErrorReporter error="Could not fetch coachable subjects" />
        </Row>
      );
    }

    const subjectOptions = new Map<string, string>(subjects.map(subject => [subject, subject]));
    const hourlyRateOptions = hourlyRatesInCents.map(hourlyRateInCents => ({
      label: currencyFormatter.format(hourlyRateInCents / 100),
      value: hourlyRateInCents,
    }));
    return (
      <>
        <Row>
          <CheckboxList
            data-test="subject-checkbox-list"
            options={subjectOptions}
            onChange={(selectedSubjects: string[]) => this.setState({ selectedSubjects })}
            checked={this.state.selectedSubjects}
          />
        </Row>
        <Row>
          <Select
            data-test="subject-hourly-rate-select"
            options={hourlyRateOptions}
            onChange={(hourlyRateInCents: string) =>
              this.setState({ hourlyRateInCents: parseInt(hourlyRateInCents) })
            }
            placeholder="Hourly Rate"
            value={this.state.hourlyRateInCents}
          />
        </Row>
      </>
    );
  };

  render() {
    const {
      loadingCoachName,
      coachName,
      hourlyRateInCents,
      coachUuid,
      submittingNewCoach,
    } = this.state;
    const inputSubtext = (loadingCoachName || coachName) && (
      <Text size="xxsmall" color="graphite" data-test="coach-name-text">
        {loadingCoachName ? <LoadingIndicator size="xsmall" /> : `Name: ${coachName}`}
      </Text>
    );

    return (
      <BoxBody size="small">
        <Row>
          <Heading level="2" serif>
            New coach
          </Heading>
        </Row>
        <Row size="small">
          <FormElement
            data-test="coach-uuid-input"
            element={Input}
            validate={e => assertions.uuid(e.target.value)}
            label="Search by UUID"
            onValidChange={e => this.setCoachUuidAndName(e.target.value)}
            subtext={inputSubtext}
            elementProps={{ value: coachUuid, placeholder: "Paste the coach's user UUID" }}
          />
        </Row>
        {this.renderCreateCoachFields()}
        <Button
          data-test="add-new-coach-button"
          loading={submittingNewCoach}
          disabled={!coachName && !coachUuid && !hourlyRateInCents}
          onClick={this.handleAddCoach}>
          Add coach
        </Button>
      </BoxBody>
    );
  }
}
